import React from 'react';
import DOMPurify from 'dompurify';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  eformDisplayContent,
  eformSpacerHeight,
  isEFormDisplayField,
  type EFormFieldSchema,
} from './schema';

const DISPLAY_HTML_PURIFY: Parameters<typeof DOMPurify.sanitize>[1] = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'div',
    'span',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'a',
    'blockquote',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
};

export function EFormDisplayBlock({ field }: { field: EFormFieldSchema }) {
  const { colors } = useTheme();
  const content = eformDisplayContent(field);

  if (field.type === 'spacer') {
    return (
      <div
        aria-hidden
        className="w-full"
        style={{ height: eformSpacerHeight(field) }}
      />
    );
  }

  if (field.type === 'heading') {
    return (
      <h3 className={cn('text-base font-semibold tracking-tight', colors.text)}>
        {content || 'Heading'}
      </h3>
    );
  }

  if (field.type === 'richtext') {
    const html = DOMPurify.sanitize(content || '<p></p>', DISPLAY_HTML_PURIFY);
    return (
      <div
        className={cn(
          'eform-display-richtext text-sm leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5',
          colors.text,
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <div
      className={cn(
        'text-sm leading-relaxed break-words',
        field.type === 'textarea' ? 'whitespace-pre-wrap' : '',
        colors.textSecondary,
      )}
    >
      {content || (field.type === 'textarea' ? 'Memo' : 'Text')}
    </div>
  );
}

export function maybeEFormDisplayNode(field: EFormFieldSchema | undefined): React.ReactNode | null {
  if (!field || !isEFormDisplayField(field)) return null;
  return <EFormDisplayBlock field={field} />;
}
